<template>
  <div
    v-if="travelerPictures?.length > 2"
    class="flex flex-col"
  >
    <ScreenWidthProvider v-slot="{ isWider, isNarrower }">
      <div class="flex justify-between mb-4 font-bold">
        <span
          data-i18n="travelerPicturesTitle"
          class="text-2xl"
        >
          {{ $t('travelerPicturesTitle') }}
        </span>
      </div>
      <div
        v-if="isWider('md', 1)"
      >
        <Swiper v-bind="desktopSliderOptions">
          <div
            v-for="(picture, index) in travelerPictures"
            :key="index"
          >
            <img
              :src="picture.thumbnail"
              :alt="`Thumbnail ${index + 1}`"
              class="rounded-lg size-full object-cover aspect-square w-full cursor-pointer"
              @click="openGallery(index)"
            />
          </div>
        </Swiper>
      </div>
      <div
        v-if="isNarrower('md')"
        class="px-6"
      >
        <SlidesBehindSlider
          backdrop-color="gray-50"
          class="clip-none"
          v-bind="mobileSliderOptions"
        >
          <div
            v-for="(picture, index) in travelerPictures"
            :key="index"
            class="thumbnail cursor-pointer"
            @click="openGallery(index)"
          >
            <img
              :src="picture.thumbnail"
              :alt="`Thumbnail ${index + 1}`"
              class="rounded-lg"
            />
          </div>
        </SlidesBehindSlider>
      </div>
      <div class="flex justify-end mt-4">
        <button
          class="text-blue-800 font-bold underline inline-flex items-center text-lg text-nowrap mt-auto"
          data-i18n="seeMoreImages"
          @click="openGallery(0)"
        >
          {{ $t('seeMoreImages') }}
          <fa
            icon="arrow-right"
            class="ml-2"
          />
        </button>
      </div>
    </ScreenWidthProvider>

    <LazyFullscreenImageCarousel
      v-if="showGallery"
      :key="hgKey"
      :images="images"
      :initial-slide="initialSlide"
      :visible="showGallery"
      @on-hide="showGallery = false"
    />
  </div>
</template>

<script setup>
const props = defineProps({
  tripTypeId: {
    type: [String, Number],
    required: true,
  },

  size: {
    type: String,
    default: '200x200',
  },

  /**
   * The fit mode for the thumbnail images.
   * @type {string}
   * @default ''
   * @example "": 'inside', // keep ratio, one side may be smaller then requested
   * @example c: 'cover', // keep ratio, match both width and height, cut of overflow
   * @example o: 'outside', // keep ratio, one side may be larger then requested
   * @example f: 'fill', // ignore ratio, match both width and height, stretch
   */
  fit: {
    type: String,
    default: '',
  },
})

const showGallery = ref(false)
const initialSlide = ref(0)

const tripStore = useTripStore()

const { data: travelerPictures } = useAsyncData(async () => {
  const result = await tripStore.fetchTravelerPicturesByTriptype(props.tripTypeId, props.size, props.fit)

  return result || []
})

const mobileSliderOptions = computed(() => ({
  pagination: true,
  navigation: true,
  dotsAlign: 'outside',
  arrowsType: 'blue',
  dotsType: 'blue',
  loadingSkeletonAnimation: 'skeleton-slides',
  loadingSkeletonAspect: false,
  loadingSkeletonHeight: '400px',
  rewind: true,
  spaceBetween: 30,
  onlyRenderVisibleSlides: true,
  preventSwiperUpdate: true,
  maxSlides: 10,
  dynamicBullets: false,
  coverflowDepth: 0,
  coverflowStretch: 50,
  constantDisplayNavigation: true,
}))

const desktopSliderOptions = computed(() => ({
  slidesPerView: 3,
  pagination: false,
  navigation: true,
  loadingSkeletonAnimation: 'skeleton-slides',
  loadingSkeletonAspect: false,
  loadingSkeletonHeight: '400px',
  rewind: true,
  onlyRenderVisibleSlides: true,
  preventSwiperUpdate: true,
  autoplay: {
    delay: 5000,
  },
  constantDisplayNavigation: true,
  arrowsType: 'blue',
  spaceBetween: 30,
  dotsType: 'blue',
  breakpoints: {
    [1000]: {
      arrowsAlign: 'border',
    },
    [1120]: {
      arrowsAlign: 'outside',
    },
    [1200]: {
      arrowsAlign: 'outside-lg',
    },
    [1250]: {
      arrowsAlign: 'outside-xl',
    },
  },
}))

const images = computed(() => {
  return travelerPictures.value?.map(picture => picture.uri) || []
})

const openGallery = (index) => {
  if (index === 'gallery') {
    initialSlide.value = 0
    showGallery.value = true
    return
  }
  initialSlide.value = index
  showGallery.value = true
}
</script>

<style lang="scss" scoped>
.thumbnail img {
  max-height: 50vw;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  cursor: pointer;
}
</style>